import { useState } from 'react';
import AdminSidebar from '../../components/AdminSidebar';
import { adminGetEnrichmentAudit } from '../../lib/adminApi';

const STEP_COLOR   = { 0: '#374151', 1: '#0176CE', 2: '#7c3aed', 3: '#ea580c', 4: '#0f766e' };
const STEP_LABEL   = { 0: 'Step 0 — Jina Pre-check', 1: 'Step 1 — Haiku (no search)', 2: 'Step 2 — Sonnet (no search)', 3: 'Step 3 — Haiku + Web Search', 4: 'Step 4 — Sonnet + Web Search' };
const JINA_MODELS  = new Set(['jina_verify', 'jina_pre_check']);

function StepBadge({ step }) {
  return (
    <span style={{ background: STEP_COLOR[step] || '#6b7280', color: '#fff', borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>
      {STEP_LABEL[step] || `Step ${step}`}
    </span>
  );
}

function ExitBadge({ exit }) {
  if (!exit) return null;
  const green = exit.includes('confirmed') || exit.includes('confident') || exit === 'final' || exit === 'is_person' || exit === 'is_internal';
  return (
    <span style={{ background: green ? '#dcfce7' : '#fef3c7', color: green ? '#16a34a' : '#d97706', borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 600, marginLeft: 8 }}>
      {exit}
    </span>
  );
}

function TokenBar({ input, output }) {
  return (
    <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#6b7280', marginTop: 8 }}>
      <span>in: <strong style={{ color: '#374151' }}>{(input || 0).toLocaleString()}</strong></span>
      <span>out: <strong style={{ color: '#374151' }}>{(output || 0).toLocaleString()}</strong></span>
      {output > 0 && <span>total: <strong style={{ color: '#374151' }}>{((input || 0) + (output || 0)).toLocaleString()}</strong></span>}
    </div>
  );
}

function JsonViewer({ text }) {
  const [expanded, setExpanded] = useState(false);
  if (!text) return <span style={{ color: '#9ca3af', fontSize: 12 }}>—</span>;

  let parsed = null;
  try { parsed = JSON.parse(text); } catch { /* not JSON */ }

  const preview = text.length > 120 ? text.slice(0, 120) + '…' : text;

  return (
    <div>
      <div
        onClick={() => setExpanded(e => !e)}
        style={{ cursor: 'pointer', fontSize: 12, color: '#374151', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 10px', fontFamily: 'monospace', whiteSpace: expanded ? 'pre-wrap' : 'nowrap', overflow: 'hidden', wordBreak: 'break-all' }}
      >
        {expanded
          ? (parsed ? JSON.stringify(parsed, null, 2) : text)
          : preview}
      </div>
      <button onClick={() => setExpanded(e => !e)} style={{ background: 'none', border: 'none', color: '#0176CE', fontSize: 11, cursor: 'pointer', padding: '4px 0', fontWeight: 600 }}>
        {expanded ? '▲ collapse' : '▼ expand'}
      </button>
    </div>
  );
}

function PromptViewer({ text }) {
  const [expanded, setExpanded] = useState(false);
  if (!text) return <span style={{ color: '#9ca3af', fontSize: 12 }}>—</span>;
  const preview = text.length > 200 ? text.slice(0, 200) + '…' : text;
  return (
    <div>
      <pre style={{ fontSize: 12, lineHeight: 1.6, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '10px 12px', whiteSpace: expanded ? 'pre-wrap' : 'nowrap', overflow: 'hidden', margin: 0, fontFamily: 'monospace', color: '#374151' }}>
        {expanded ? text : preview}
      </pre>
      <button onClick={() => setExpanded(e => !e)} style={{ background: 'none', border: 'none', color: '#0176CE', fontSize: 11, cursor: 'pointer', padding: '4px 0', fontWeight: 600 }}>
        {expanded ? '▲ collapse prompt' : '▼ expand prompt'}
      </button>
    </div>
  );
}

function StepCard({ step }) {
  const [open, setOpen] = useState(true);
  const isJina = JINA_MODELS.has(step.model || '');

  if (isJina) {
    return (
      <div style={s.stepCard}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ background: '#f0f9ff', border: '1px solid #bae6fd', color: '#0369a1', borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>
            {step.model === 'jina_pre_check' ? 'Jina Pre-check' : 'Jina Verify'}
          </span>
          <ExitBadge exit={step.exit_reason} />
          {step.domain_found && <span style={{ fontSize: 12, color: '#16a34a', fontWeight: 600 }}>{step.domain_found}</span>}
        </div>
      </div>
    );
  }

  return (
    <div style={s.stepCard}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }} onClick={() => setOpen(o => !o)}>
        <StepBadge step={step.step} />
        <ExitBadge exit={step.exit_reason} />
        {step.domain_found && (
          <span style={{ fontSize: 12, color: '#16a34a', fontWeight: 600 }}>
            {step.domain_found} {step.domain_confidence ? `(${step.domain_confidence}%)` : ''}
          </span>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 11, color: '#9ca3af' }}>
          {step.duration_ms ? `${(step.duration_ms / 1000).toFixed(1)}s` : ''}
        </span>
        <span style={{ fontSize: 13, color: '#6b7280' }}>{open ? '▲' : '▼'}</span>
      </div>

      {open && (
        <div style={{ marginTop: 16 }}>
          <div style={s.paneGrid}>
            <div>
              <div style={s.paneLabel}>Prompt</div>
              <PromptViewer text={step.prompt_text} />
            </div>
            <div>
              <div style={s.paneLabel}>Response</div>
              <JsonViewer text={step.response_text} />
              <TokenBar input={step.input_tokens} output={step.output_tokens} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ResultCard({ result }) {
  if (!result) return <div style={{ color: '#9ca3af', fontSize: 13, padding: '12px 0' }}>No result row found</div>;

  const fields = [
    ['Name (std)',     result.name1_std],
    ['DBA',           result.name2_std],
    ['Address',       [result.address1_std, result.city_std, result.state_std, result.country_std].filter(Boolean).join(', ')],
    ['Phone',         result.phone_formatted],
    ['Email',         result.email_std],
    ['Domain',        result.domain_value],
    ['Confidence',    result.domain_confidence],
    ['Source',        result.domain_source],
    ['NAICS',         result.naics_code ? `${result.naics_code} — ${result.naics_title || ''} (${result.naics_confidence || '?'}%)` : null],
    ['What they do',  result.what_they_do],
    ['Tokens used',   result.total_tokens?.toLocaleString()],
    ['Processed at',  result.processed_at ? new Date(result.processed_at).toLocaleString() : null],
  ];

  return (
    <div style={s.resultCard}>
      <div style={s.paneLabel}>Final Result — {result.name_raw}</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px', marginTop: 8 }}>
        {fields.map(([label, value]) => value ? (
          <div key={label} style={s.detailRow}>
            <span style={s.detailLabel}>{label}</span>
            <span style={s.detailValue}>{value}</span>
          </div>
        ) : null)}
      </div>
    </div>
  );
}

function RecordCard({ record }) {
  const [open, setOpen] = useState(true);
  const exitStep = record.steps[record.steps.length - 1]?.exit_reason;

  return (
    <div style={s.recordCard}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', padding: '16px 20px', background: '#f8fafc', borderRadius: '10px 10px 0 0' }} onClick={() => setOpen(o => !o)}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#0a2540' }}>Job #{record.job_id} — {record.file_name}</div>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{record.tenant_name} · Row {record.row_number} · {record.steps.length} steps</div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center' }}>
          {record.result?.domain_value && (
            <span style={{ fontSize: 13, fontWeight: 600, color: '#16a34a' }}>{record.result.domain_value}</span>
          )}
          <span style={{ fontSize: 12, color: '#9ca3af', background: '#e5e7eb', borderRadius: 4, padding: '2px 8px' }}>{record.job_status}</span>
          <span style={{ color: '#6b7280' }}>{open ? '▲' : '▼'}</span>
        </div>
      </div>

      {open && (
        <div style={{ padding: '0 20px 20px' }}>
          <ResultCard result={record.result} />
          <div style={{ marginTop: 16 }}>
            <div style={s.paneLabel}>Enrichment Steps</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 8 }}>
              {record.steps.map((step, i) => <StepCard key={i} step={step} />)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminEnrichmentAudit() {
  const [vendorNum,  setVendorNum]  = useState('');
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState(null);
  const [auditData,  setAuditData]  = useState(null);

  const search = async () => {
    if (!vendorNum.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const data = await adminGetEnrichmentAudit(vendorNum.trim());
      setAuditData(data);
    } catch (e) {
      setError('Failed to load: ' + (e?.message || e));
    } finally {
      setLoading(false);
    }
  };

  const handleKey = e => { if (e.key === 'Enter') search(); };

  return (
    <div style={s.page}>
      <AdminSidebar active="enrichment-audit" />
      <main style={s.main}>

        <div style={s.header}>
          <h1 style={s.title}>Enrichment Audit</h1>
          <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>Search by vendor # to see every Claude prompt and response across all jobs</div>
        </div>

        <div style={s.searchRow}>
          <input
            value={vendorNum}
            onChange={e => setVendorNum(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Vendor / customer number…"
            style={s.input}
          />
          <button onClick={search} disabled={loading} style={s.btn}>
            {loading ? 'Searching…' : 'Search'}
          </button>
        </div>

        {error && <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 16 }}>{error}</div>}

        {auditData && auditData.records.length === 0 && (
          <div style={{ color: '#6b7280', fontSize: 14, padding: '40px 0', textAlign: 'center' }}>
            No enrichment records found for vendor # <strong>{auditData.vendor_num}</strong>
          </div>
        )}

        {auditData && auditData.records.length > 0 && (
          <div>
            <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
              Found <strong style={{ color: '#0a2540' }}>{auditData.records.length}</strong> job{auditData.records.length !== 1 ? 's' : ''} for vendor # <strong style={{ color: '#0a2540' }}>{auditData.vendor_num}</strong>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {auditData.records.map((record, i) => <RecordCard key={i} record={record} />)}
            </div>
          </div>
        )}

      </main>
    </div>
  );
}

const s = {
  page:        { display: 'flex', minHeight: '100vh', background: '#f8fafc' },
  main:        { flex: 1, padding: '36px 40px', overflowY: 'auto' },
  header:      { marginBottom: 24 },
  title:       { fontSize: 22, fontWeight: 700, color: '#0a2540', margin: 0 },
  searchRow:   { display: 'flex', gap: 10, marginBottom: 24, maxWidth: 500 },
  input:       { flex: 1, padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none' },
  btn:         { background: '#0176CE', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  recordCard:  { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' },
  stepCard:    { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 16px' },
  resultCard:  { background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '14px 16px', marginTop: 16 },
  paneGrid:    { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 },
  paneLabel:   { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#94a3b8', marginBottom: 8 },
  detailRow:   { display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0', padding: '5px 0', fontSize: 12 },
  detailLabel: { color: '#6b7280' },
  detailValue: { color: '#0a2540', fontWeight: 500, maxWidth: '55%', textAlign: 'right', wordBreak: 'break-all' },
};
